<template>
  <!-- Loader -->
  <div v-if="loading" class="d-flex justify-content-center align-items-center" style="min-height:220px">
    <div class="spinner-border text-primary" role="status" style="width:5rem;height:5rem">
      <span class="visually-hidden">Chargement...</span>
    </div>
  </div>

  <div
    class="card mb-25 border-0 rounded-0 bg-white letter-spacing"
    :style="loading ? { 'pointer-events': 'none', opacity: '0.08' } : {}"
  >
    <!-- ── Header ── -->
    <div class="p-15 p-md-20 border-bottom d-flex flex-wrap align-items-center justify-content-between gap-2">
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <h5 class="mb-0 fw-bold">
          <i class="flaticon-users-group me-2 text-primary"></i> Clients
        </h5>
        
      </div>

      <div class="d-flex align-items-center gap-2 flex-wrap">
        <!-- Search -->
        <div class="position-relative">
          <i
            class="flaticon-search position-absolute top-50 translate-middle-y text-muted"
            style="left:10px; font-size:13px; pointer-events:none"
          ></i>
          <input
            type="text"
            class="form-control form-control-sm rounded-1 ps-4"
            style="min-width:240px"
            placeholder="Rechercher un client..."
            v-model="users.searchTerm"
            @input="handleSearch"
          />
          <button
            v-if="users.searchTerm"
            class="btn btn-sm position-absolute top-50 translate-middle-y end-0 me-1 p-0 border-0 bg-transparent text-muted"
            @click="users.searchTerm = ''; handleSearch()"
          >
            <i class="ri-close-line"></i>
          </button>
        </div>

        <!-- Load colis / factures totals on demand -->
        <button
          class="btn btn-outline-primary btn-sm fw-medium d-flex align-items-center gap-1"
          :disabled="countsLoading || rows.length === 0"
          @click="fetchCounts(rows.map((r) => r.id))"
        >
          <span v-if="countsLoading" class="spinner-border spinner-border-sm"></span>
          <i v-else class="ri-bar-chart-2-line"></i>
          Afficher colis / factures
        </button>

        <!-- Add -->
        <router-link
          v-if="auth.can('ADD_CUSTOMER')"
          to="/add-customer"
          class="btn btn-success btn-sm fw-medium d-flex align-items-center gap-1"
        >
          <i class="flaticon-plus fs-12"></i> Nouveau client
        </router-link>
      </div>
    </div>

    <!-- ── Table ── -->
    <div class="card-body p-0">
       <div class="table-scroll-outer">
        <div class="table-scroll-mobile">
        <table class="table text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <th class="ps-20 text-uppercase fs-12 text-muted fw-semibold" style="width:52px">#</th>
              <th class="text-uppercase fs-12 text-muted fw-semibold" style="width:56px">Logo</th>
              <th class="text-uppercase fs-12 text-muted fw-semibold">Magasin / Infos</th>
              <th class="text-uppercase fs-12 text-muted fw-semibold">Contact</th>
              <th class="text-uppercase fs-12 text-muted fw-semibold">Statut</th>
              <th class="text-uppercase fs-12 text-muted fw-semibold sortable" @click="toggleSort('colis')">
                Colis <i :class="sortIcon('colis')"></i>
              </th>
              <th class="text-uppercase fs-12 text-muted fw-semibold sortable" @click="toggleSort('invoices')">
                Factures <i :class="sortIcon('invoices')"></i>
              </th>
              <th class="text-uppercase fs-12 text-muted fw-semibold">Création</th>
              <th class="text-uppercase fs-12 text-muted fw-semibold text-end pe-20">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="rows.length === 0 && !loading">
              <td colspan="9" class="text-center py-40 text-muted">
                <i class="flaticon-users-group fs-40 d-block mb-10 opacity-25"></i>
                Aucun client trouvé
              </td>
            </tr>

            <tr
              v-for="(row, i) in rows"
              :key="row.id"
              class="border-top customer-row"
            >
              <!-- # -->
              <td class="ps-20 text-muted fs-13">
                {{ (users.current_page - 1) * users.per_page + i + 1 }}
              </td>

              <!-- Avatar -->
              <td>
                <img
                  v-if="row.logo && !imgErrors[row.id]"
                  :src="row.logo"
                  class="customer-avatar"
                  :alt="row.store"
                  @error="imgErrors[row.id] = true"
                />
                <div
                  v-else
                  class="customer-avatar-letter"
                  :style="{ background: getColor(row.store) }"
                >
                  {{ firstLetter(row.store) }}
                </div>
              </td>

              <!-- Magasin / name -->
              <td>
                <span class="fw-semibold fs-14 d-block">{{ row.store }}</span>
                <span class="text-muted fs-12">{{ row.name }}</span>
                <span v-if="row.store_name" class="text-muted fs-12 ms-1">({{ row.store_name }})</span>
              </td>

              <!-- Contact -->
              <td class="fs-13">
                <div class="d-flex flex-column gap-1">
                  <span v-if="row.phone">
                    <i class="ri-phone-line text-muted me-1"></i>{{ row.phone }}
                  </span>
                  <span v-if="row.email" class="text-muted">
                    <i class="ri-mail-line me-1"></i>{{ row.email }}
                  </span>
                  <span v-if="row.city" class="text-muted">
                    <i class="ri-map-pin-line me-1"></i>{{ row.city }}
                  </span>
                </div>
              </td>

              <!-- Active toggle -->
              <td>
                <div class="switch">
                  <input
                    type="checkbox"
                    class="switch-input"
                    :id="'sw-' + row.id"
                    :checked="row.active"
                    @change="toggleAdActive(row)"
                  />
                  <label class="switch-label" :for="'sw-' + row.id"></label>
                </div>
                <span class="d-block fs-11 mt-1" :class="row.active ? 'text-success' : 'text-muted'">
                  {{ row.active ? 'Actif' : 'Inactif' }}
                </span>
              </td>

              <!-- Colis / Factures (lazy counts) -->
              <td class="fs-13">
                <span v-if="counts.colis[row.id] !== undefined">{{ counts.colis[row.id] }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="fs-13">
                <span v-if="counts.invoices[row.id] !== undefined">{{ counts.invoices[row.id] }}</span>
                <span v-else class="text-muted">—</span>
              </td>

              <!-- Date -->
              <td class="text-muted fs-13">{{ getDate(row.created_at) }}</td>

              <!-- Actions -->
              <td class="text-end pe-20">
                <div class="d-flex align-items-center justify-content-end gap-1">
                  <button
                    v-if="auth.can('CUSTOMERS') && auth.can('CONNEXION_CUSTOMER')"
                    class="action-btn action-btn--primary"
                    title="Login client"
                    @click="adminLogin(row)"
                  >
                    <i class="ph ph-sign-in"></i>
                  </button>
                  <button
                    v-if="auth.can('CUSTOMERS') && auth.can('EDIT_CUSTOMER')"
                    class="action-btn action-btn--warning"
                    title="Modifier"
                    @click="$router.push({ name: 'EditCustomerPage', params: { id: row.id } })"
                  >
                    <i class="flaticon-pen"></i>
                  </button>
                  <button
                    v-if="auth.can('CUSTOMERS')"
                    class="action-btn action-btn--info"
                    title="Historique"
                    @click="$router.push({ name: 'HistoryCustomerPage', params: { id: row.id } })"
                  >
                    <i class="flaticon-clock"></i>
                  </button>
                  <button
                    v-if="auth.can('CUSTOMERS')"
                    class="action-btn action-btn--secondary"
                    title="Équipes"
                    @click="$router.push({ name: 'StaffCustomerPage', params: { id: row.id } })"
                  >
                    <i class="ph ph-users-four"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      </div>

      <!-- Pagination -->
      <div class="px-15 px-md-20 py-12 border-top">
        <PaginatorContent
          :pagination="users"
          :offset="9"
          @paginate="fetchData"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useAppToast } from "@/composables/useAppToast";
import api from "@/utils/axios";
import moment from "moment";
import Swal from "sweetalert2";
import { defineComponent, onMounted, reactive, ref } from "vue";
import { useAuthStore } from "@/stores/auth";

const COLORS = ["#1abc9c","#3498db","#9b59b6","#e67e22","#e74c3c","#2ecc71","#f39c12","#16a085","#8e44ad","#d35400"];

export default defineComponent({
  name: "CustomersList",
  setup() {
    const { showToast } = useAppToast();
    const auth = useAuthStore();

    const rows = ref([]);
    const loading = ref(false);
    const imgErrors = reactive<Record<number, boolean>>({});

    const countsLoading = ref(false);
    const sort = reactive({ by: "" as "" | "colis" | "invoices", dir: "desc" as "asc" | "desc" });
    const counts = reactive<{ colis: Record<number, number>; invoices: Record<number, number> }>({ colis: {}, invoices: {} });

    const users = reactive({
      searchName: "",
      searchCity: "",
      searchMagasin: "",
      searchZone: "",
      searchAdmin: "",
      searchCDates: "",
      searchCDatef: "",
      searchAccount: "",
      searchSlDate: "",
      searchElDate: "",
      searchTerm: "",
      searchCoupon: "",
      searchStores: "",
      total: 0,
      per_page: 10,
      from: 1,
      to: 0,
      current_page: 1,
    });

    const fetchData = async (page = 1) => {
      loading.value = true;
      users.current_page = page;
      try {
        const res = await api.get(`/api/auth/customer_list`, {
          params: {
            searchTerm: users.searchTerm,
            searchName: users.searchName,
            searchSlDate: users.searchSlDate,
            searchElDate: users.searchElDate,
            searchCity: users.searchCity,
            searchMagasin: users.searchMagasin,
            searchZone: users.searchZone,
            searchAdmin: users.searchAdmin,
            searchCDates: users.searchCDates,
            searchCDatef: users.searchCDatef,
            searchAccount: users.searchAccount,
            searchCoupon: users.searchCoupon,
            searchStores: users.searchStores,
            page: users.current_page,
            per_page: users.per_page,
            sort_by: sort.by || undefined,
            sort_dir: sort.by ? sort.dir : undefined,
          },
        });
        rows.value = res.data.users.data;
        Object.assign(users, res.data.users);
        counts.colis = {};
        counts.invoices = {};
      } catch (err) {
        console.error(err);
      } finally {
        loading.value = false;
      }
    };

    // Counts are loaded only when the user clicks the button, for the visible page only.
    const fetchCounts = async (ids: number[]) => {
      if (!ids.length) return;
      countsLoading.value = true;
      try {
        const res = await api.get(`/api/auth/customer_counts`, { params: { ids: ids.join(",") } });
        counts.colis = res.data.colis || {};
        counts.invoices = res.data.invoices || {};
      } catch (err) {
        console.error(err);
      } finally {
        countsLoading.value = false;
      }
    };

    const toggleSort = (key: "colis" | "invoices") => {
      if (sort.by !== key) {
        sort.by = key;
        sort.dir = "desc";
      } else if (sort.dir === "desc") {
        sort.dir = "asc";
      } else {
        sort.by = "";
        sort.dir = "desc";
      }
      fetchData(1);
    };

    const sortIcon = (key: string) =>
      sort.by !== key ? "ri-arrow-up-down-line opacity-50" : sort.dir === "asc" ? "ri-arrow-up-line text-primary" : "ri-arrow-down-line text-primary";

    onMounted(() => fetchData());

    function getDate(value: any) {
      return moment.utc(String(value)).format("DD/MM/YYYY HH:mm");
    }

    function firstLetter(name: string) {
      return name ? name.charAt(0).toUpperCase() : "?";
    }

    function getColor(name: string) {
      if (!name) return "#6c757d";
      let hash = 0;
      for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
      return COLORS[Math.abs(hash) % COLORS.length];
    }

    async function toggleAdActive(row: any) {
      const originalValue = row.active;
      Swal.fire({
        text: row.active ? "Vous voulez désactiver ce compte ?" : "Vous voulez activer ce compte ?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: row.active ? "Oui, désactiver!" : "Oui, activer!",
        cancelButtonText: "Annuler",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const res = await api.post(`/api/auth/customer_active/${row.id}`, {
              active: Number(!row.active),
            });
            if (res.data.message === "success") {
              fetchData(users.current_page);
              showToast("Client modifié avec succès", "success");
            }
          } catch {
            showToast("Erreur lors de la modification", "error");
            row.active = originalValue;
          }
        } else {
          row.active = originalValue;
        }
      });
    }

    let searchTimeout: ReturnType<typeof setTimeout> | null = null;
    function handleSearch() {
      if (searchTimeout) clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => fetchData(1), 400);
    }

    async function adminLogin(customer: any) {
      try {
        const res = await api.get(`/api/auth/customer_login/${customer.id}`);
        const d = res.data;

        if (d.msg === "unauthorized") {
          showToast("Action non autorisée", "error");
        } else if (d.msg === "inactive") {
          showToast("Ce client est désactivé", "error");
        } else if (d.msg === "not found") {
          showToast("Client introuvable", "error");
        } else if (d.msg === "success") {
          // Only the token crosses the URL — the client app fetches the
          // user/permissions itself using it as a Bearer credential, instead
          // of trusting a client-decoded blob of user+permissions handed to
          // it in plain sight (browser history, server/proxy access logs,
          // shoulder-surfing the address bar).
          window.open(
            `${process.env.VUE_APP_DOMAIN_CUSTOMER}/?staff_token=${encodeURIComponent(d.access_token)}`,
            "_blank"
          );
        } else {
          showToast("Erreur lors de la connexion", "error");
        }
      } catch (error) {
        console.error(error);
        showToast("Erreur serveur, réessayez plus tard", "error");
      }
    }

    return {
      headers: [],
      rows,
      users,
      loading,
      imgErrors,
      counts,
      countsLoading,
      fetchCounts,
      toggleSort,
      sortIcon,
      fetchData,
      handleSearch,
      getDate,
      firstLetter,
      getColor,
      toggleAdActive,
      adminLogin,
      auth,
    };
  },
});
</script>

<style scoped>
thead th {
  background: #f8f9fa;
  padding: 10px 14px;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}
tbody td { padding: 11px 14px; }
thead th.sortable { cursor: pointer; user-select: none; }
thead th.sortable:hover { color: #0d6efd !important; }

.customer-row:hover { background: #f5f8ff; }

/* ── Avatar ── */
.customer-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e9ecef;
}
.customer-avatar-letter {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
  color: #fff;
  flex-shrink: 0;
}

/* ── Action buttons ── */
.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: none;
  font-size: 14px;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.12s;
}
.action-btn:hover { opacity: 0.82; transform: scale(1.08); }

.action-btn--primary   { background: #e8f0fe; color: #0d6efd; }
.action-btn--warning   { background: #fff8e1; color: #e67e22; }
.action-btn--info      { background: #e3f2fd; color: #0288d1; }
.action-btn--secondary { background: #f3f4f6; color: #6c757d; }
</style>
